/* Order Tracker — controller (state machine, fetch, fallbacks)
 * Lazy-loads the globe renderer (assets/order-tracker-globe.js) only after submit.
 * No external dependencies; the globe module pulls Three.js + three-globe from CDN.
 */
(function () {
  'use strict';

  // Mr Football's China → Lebanon timeline.
  var STATUS_TO_PROGRESS = {
    order_placed: 0.00,
    in_production: 0.20,
    shipped: 0.40,
    shipped_to_lebanon: 0.60,
    in_lebanon_customs: 0.80,
    delivered_warehouse: 1.00,
    exception: null
  };
  var STEP_ORDER = ['order_placed', 'in_production', 'shipped', 'shipped_to_lebanon', 'in_lebanon_customs', 'delivered_warehouse'];

  var globeModulePromise = null;

  /* -------------------------------------------------------------------------
   * Demo mode — offline showcase.
   * When no live backend is connected yet (App Proxy not deployed, network
   * failure, or a blank endpoint), we synthesise a realistic, good-looking
   * tracking result so the section is fully functional out of the box.
   * The data is deterministic per tracking number, so the same input always
   * shows the same journey. Once the real /apps/track backend is live, real
   * data takes over automatically and this is never shown.
   * ---------------------------------------------------------------------- */
  // Origin reflects the real business flow: parcels ship from China to the customer.
  var DEMO_ORIGIN = { label: 'Guangzhou, China', lat: 23.1291, lng: 113.2644 };
  var DEMO_DESTINATIONS = [
    { label: 'Beirut, Lebanon',              lat: 33.8938, lng: 35.5018 },
    { label: 'Tripoli, Lebanon',             lat: 34.4367, lng: 35.8497 },
    { label: 'Saida, Lebanon',               lat: 33.5571, lng: 35.3729 },
    { label: 'Jounieh, Lebanon',             lat: 33.9808, lng: 35.6178 },
    { label: 'Zahle, Lebanon',               lat: 33.8463, lng: 35.9019 },
    { label: 'Dubai, United Arab Emirates',  lat: 25.2048, lng: 55.2708 },
    { label: 'Riyadh, Saudi Arabia',         lat: 24.7136, lng: 46.6753 }
  ];
  var DEMO_HUBS = [
    { label: 'Hong Kong',                    lat: 22.3193, lng: 114.1694 },
    { label: 'Dubai, United Arab Emirates',  lat: 25.2528, lng: 55.3644 },
    { label: 'Istanbul, Türkiye',            lat: 41.0082, lng: 28.9784 }
  ];
  var DEMO_CARRIERS = ['YunExpress', '4PX', 'China Post', 'Aramex', 'DHL Express'];
  var DEMO_FLOW = ['in_production', 'shipped', 'shipped_to_lebanon', 'in_lebanon_customs', 'delivered_warehouse'];
  var DEMO_PRODUCTS = [
    'Mercurial Superfly Elite FG',
    'Home Kit 2026',
    'Match Pro Football',
    'Goalkeeper Gloves',
    'Phantom GX Pro',
    'Training Shorts'
  ];

  function demoHash(str) {
    var h = 2166136261;
    str = String(str || 'MRF');
    for (var i = 0; i < str.length; i++) {
      h ^= str.charCodeAt(i);
      h = (h * 16777619) >>> 0;
    }
    return h >>> 0;
  }

  function lerp(a, b, t) { return a + (b - a) * t; }

  // A believable China-style tracking number derived from the seed.
  function demoTrackingNumber(h) {
    var digits = '';
    var x = h;
    for (var i = 0; i < 9; i++) { digits += (x % 10); x = Math.floor(x / 10) || (h >>> (i + 1)); }
    return 'LP' + digits + 'CN';
  }

  function buildDemoData(seed, config) {
    var h = demoHash(seed);
    var origin = { label: DEMO_ORIGIN.label, lat: DEMO_ORIGIN.lat, lng: DEMO_ORIGIN.lng };
    var dest = DEMO_DESTINATIONS[h % DEMO_DESTINATIONS.length];
    var hub = DEMO_HUBS[(h >>> 3) % DEMO_HUBS.length];
    var status = DEMO_FLOW[(h >>> 6) % DEMO_FLOW.length];
    var progress = STATUS_TO_PROGRESS[status];
    if (typeof progress !== 'number') progress = 0.6;

    var current;
    if (status === 'delivered_warehouse' || status === 'in_lebanon_customs' || status === 'shipped_to_lebanon') {
      current = { label: dest.label, lat: dest.lat, lng: dest.lng };
    } else if (progress <= 0.2) {
      current = { label: origin.label, lat: origin.lat, lng: origin.lng };
    } else {
      current = { label: hub.label, lat: lerp(origin.lat, dest.lat, progress), lng: lerp(origin.lng, dest.lng, progress) };
    }

    var carrier = DEMO_CARRIERS[(h >>> 9) % DEMO_CARRIERS.length];
    var now = Date.now();
    var DAY = 86400000, HOUR = 3600000;
    var placedAt = now - (6 + (h % 5)) * DAY;
    var eta = new Date(now + (2 + (h % 6)) * DAY).toISOString();
    var lastUpdate = new Date(now - (2 + (h % 20)) * HOUR).toISOString();

    var flowSteps = [
      { label: 'Order placed',                          loc: origin.label },
      { label: 'In production at supplier',             loc: origin.label },
      { label: 'Shipped from China by ' + carrier,      loc: origin.label },
      { label: 'Arrived in Lebanon',                    loc: dest.label },
      { label: 'Clearing Lebanon customs',              loc: dest.label },
      { label: 'Delivered to our warehouse',            loc: dest.label }
    ];
    var idx = STEP_ORDER.indexOf(status);
    if (idx < 0) idx = 3;
    var events = [];
    for (var i = 0; i <= idx; i++) {
      var ts = new Date(placedAt + (i * (now - placedAt) / (idx + 1))).toISOString();
      events.unshift({ ts: ts, label: flowSteps[i].label, location: flowSteps[i].loc });
    }

    // 1–2 sample products so the "Your order" panel looks complete in demo mode.
    var products = [{ title: DEMO_PRODUCTS[h % DEMO_PRODUCTS.length], quantity: 1 }];
    if ((h >>> 12) % 3 === 0) {
      var second = DEMO_PRODUCTS[(h >>> 15) % DEMO_PRODUCTS.length];
      if (second !== products[0].title) products.push({ title: second, quantity: 1 });
    }

    return {
      status: status,
      progress: progress,
      trackingNumber: demoTrackingNumber(h),
      orderPlacedAt: new Date(placedAt).toISOString(),
      carrier: carrier,
      eta: eta,
      lastUpdate: lastUpdate,
      lastLocation: current.label,
      origin: origin,
      destination: dest,
      current: current,
      events: events,
      products: products,
      _demo: true
    };
  }

  function loadScript(url) {
    return new Promise(function (resolve, reject) {
      // Reuse if already loaded
      var existing = document.querySelector('script[data-ot-src="' + url + '"]');
      if (existing) {
        if (existing.getAttribute('data-loaded') === '1') return resolve();
        existing.addEventListener('load', function () { resolve(); });
        existing.addEventListener('error', function () { reject(new Error('Failed to load ' + url)); });
        return;
      }
      var s = document.createElement('script');
      s.src = url;
      s.async = false;
      s.setAttribute('data-ot-src', url);
      s.onload = function () { s.setAttribute('data-loaded', '1'); resolve(); };
      s.onerror = function () { reject(new Error('Failed to load ' + url)); };
      document.head.appendChild(s);
    });
  }

  function loadGlobeModule(url) {
    if (!globeModulePromise) {
      // Load Three.js + OrbitControls + three-globe + our renderer (in order, classic scripts).
      var THREE_VER = '0.146.0';
      var GLOBE_VER = '2.27.0';
      globeModulePromise = loadScript('https://unpkg.com/three@' + THREE_VER + '/build/three.min.js')
        .then(function () { return loadScript('https://unpkg.com/three@' + THREE_VER + '/examples/js/controls/OrbitControls.js'); })
        .then(function () { return loadScript('https://unpkg.com/three-globe@' + GLOBE_VER + '/dist/three-globe.min.js'); })
        .then(function () { return loadScript(url); })
        .then(function () {
          if (!window.OrderTrackerGlobe) throw new Error('OrderTrackerGlobe global missing after load');
          return window.OrderTrackerGlobe;
        })
        .catch(function (err) {
          globeModulePromise = null;
          throw err;
        });
    }
    return globeModulePromise;
  }

  function detectWebGL() {
    try {
      var c = document.createElement('canvas');
      return !!(window.WebGLRenderingContext && (c.getContext('webgl') || c.getContext('experimental-webgl')));
    } catch (e) { return false; }
  }

  function reducedMotion() {
    return window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  function isEmail(s) { return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(s); }
  function isPhone(s) { return /^\+?[0-9\s\-()]{6,}$/.test(s); }

  function setState(root, name) {
    var states = root.querySelectorAll('.ot-state');
    for (var i = 0; i < states.length; i++) {
      states[i].setAttribute('data-active', states[i].getAttribute('data-state') === name ? 'true' : 'false');
    }
  }

  function escapeHtml(s) {
    return String(s == null ? '' : s).replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  }

  function fmtDate(iso) {
    if (!iso) return '—';
    try {
      var d = new Date(iso);
      if (isNaN(d.getTime())) return iso;
      return d.toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' });
    } catch (e) { return iso; }
  }

  function fmtDateOnly(iso) {
    if (!iso) return '—';
    try {
      var d = new Date(iso);
      if (isNaN(d.getTime())) return iso;
      return d.toLocaleDateString(undefined, { dateStyle: 'medium' });
    } catch (e) { return iso; }
  }

  // Expected delivery window = order-placed date + 10 to 18 days, shown as a
  // date range. Falls back to "10–18 days" when the order date is unavailable.
  function expectedDelivery(placedIso) {
    if (!placedIso) return '10–18 days';
    var base = new Date(placedIso);
    if (isNaN(base.getTime())) return '10–18 days';
    var DAY = 86400000;
    var from = new Date(base.getTime() + 10 * DAY);
    var to = new Date(base.getTime() + 18 * DAY);
    try {
      var sameYear = from.getFullYear() === to.getFullYear();
      var fromStr = from.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: sameYear ? undefined : 'numeric' });
      var toStr = to.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
      return fromStr + ' – ' + toStr;
    } catch (e) { return '10–18 days'; }
  }

  function paintTimeline(root, status, progress) {
    var timeline = root.querySelector('[data-ot-timeline]');
    if (!timeline) return;
    var activeIndex = STEP_ORDER.indexOf(status);
    if (activeIndex < 0 && typeof progress === 'number') {
      activeIndex = Math.max(0, Math.min(STEP_ORDER.length - 1, Math.round(progress * (STEP_ORDER.length - 1))));
    }
    if (activeIndex < 0) activeIndex = 0;

    var steps = timeline.querySelectorAll('.ot-step');
    for (var i = 0; i < steps.length; i++) {
      if (i < activeIndex) steps[i].setAttribute('data-state', 'done');
      else if (i === activeIndex) steps[i].setAttribute('data-state', 'active');
      else steps[i].setAttribute('data-state', 'pending');
    }
    var frac = activeIndex / (STEP_ORDER.length - 1);
    timeline.style.setProperty('--ot-progress-frac', frac.toFixed(3));
    timeline.style.setProperty('--ot-progress', (frac * 100).toFixed(1) + '%');

    // Banner status pill — show the active step label
    var pill = root.querySelector('[data-ot-status-pill]');
    if (pill) {
      var activeStep = steps[activeIndex];
      var label = activeStep ? activeStep.querySelector('.ot-step-label') : null;
      if (label) pill.textContent = label.textContent;
    }
  }

  function paintInfo(root, data) {
    var info = root.querySelector('[data-ot-info]');
    if (!info) return;
    var delivered = data.status === 'delivered_warehouse';
    var fields = {
      trackingNumber: data.trackingNumber || '—',
      carrier: data.carrier || '—',
      eta: delivered ? fmtDateOnly(data.deliveredAt || data.lastUpdate) : expectedDelivery(data.orderPlacedAt),
      lastUpdate: fmtDate(data.lastUpdate),
      lastLocation: data.lastLocation || '—'
    };
    // When delivered, relabel the "Expected delivery" cell to "Delivered".
    var etaLabel = root.querySelector('[data-ot-eta-label]');
    if (etaLabel) etaLabel.textContent = delivered ? 'Delivered' : 'Expected delivery';
    Object.keys(fields).forEach(function (k) {
      var el = info.querySelector('[data-field="' + k + '"]');
      if (el) el.textContent = fields[k];
    });
    // Banner tracking number
    var bannerNum = root.querySelector('[data-ot-banner-number]');
    if (bannerNum) bannerNum.textContent = data.trackingNumber || '—';
  }

  function paintEvents(root, events) {
    var ul = root.querySelector('[data-ot-events]');
    if (!ul) return;
    if (!events || !events.length) { ul.innerHTML = ''; return; }
    var html = '';
    for (var i = 0; i < events.length; i++) {
      var ev = events[i];
      html += '<li class="ot-event">'
        + '<div class="ot-event-row">'
          + '<span class="ot-event-time">' + escapeHtml(fmtDate(ev.ts)) + '</span>'
          + (ev.location ? '<span class="ot-event-loc">· ' + escapeHtml(ev.location) + '</span>' : '')
        + '</div>'
        + '<div class="ot-event-label">' + escapeHtml(ev.label || '') + '</div>'
        + '</li>';
    }
    ul.innerHTML = html;
  }

  function paintProducts(root, products) {
    var wrap = root.querySelector('[data-ot-products-wrap]');
    var ul = root.querySelector('[data-ot-products]');
    if (!wrap || !ul) return;
    if (!products || !products.length) {
      wrap.hidden = true;
      ul.innerHTML = '';
      return;
    }
    var html = '';
    for (var i = 0; i < products.length; i++) {
      var p = products[i] || {};
      var qty = (typeof p.quantity === 'number' && p.quantity > 0) ? p.quantity : 1;
      var img = p.image
        ? '<img class="ot-product-img" src="' + escapeHtml(p.image) + '" alt="" loading="lazy" width="56" height="56">'
        : '<span class="ot-product-img ot-product-img--empty" aria-hidden="true"></span>';
      html += '<li class="ot-product">'
        + img
        + '<span class="ot-product-title">' + escapeHtml(p.title || '') + '</span>'
        + '<span class="ot-product-qty">×' + qty + '</span>'
        + '</li>';
    }
    ul.innerHTML = html;
    wrap.hidden = false;
  }

  function carrierFallback(root, data, strings) {
    var aTrack = root.querySelector('[data-ot-carrier-link]');
    var aErr = root.querySelector('[data-ot-error-carrier]');
    var url = data && data.carrierTrackingUrl;
    if (aTrack) {
      if (url) { aTrack.href = url; aTrack.hidden = false; }
      else aTrack.hidden = true;
    }
    if (aErr) {
      if (url) { aErr.href = url; aErr.hidden = false; }
      else aErr.hidden = true;
    }
  }

  function showError(root, message, carrierUrl) {
    var msgEl = root.querySelector('[data-ot-error-msg]');
    if (msgEl) msgEl.textContent = message;
    var aErr = root.querySelector('[data-ot-error-carrier]');
    if (aErr) {
      if (carrierUrl) { aErr.href = carrierUrl; aErr.hidden = false; }
      else aErr.hidden = true;
    }
    setState(root, 'error');
  }

  function fetchTracking(endpoint, body) {
    if (!endpoint) {
      return Promise.reject({ code: 'no_endpoint' });
    }
    return fetch(endpoint, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify(body),
      credentials: 'same-origin'
    }).then(function (res) {
      return res.json().then(function (json) {
        if (!res.ok) {
          var code = (json && json.error) || ('http_' + res.status);
          return Promise.reject({ code: code, status: res.status, body: json });
        }
        return json;
      }, function () {
        return Promise.reject({ code: 'invalid_response', status: res.status });
      });
    }, function () {
      return Promise.reject({ code: 'network_error' });
    });
  }

  function errorCodeToMessage(code, strings) {
    switch (code) {
      case 'order_not_found':       return strings.err_not_found;
      case 'no_tracking_yet':       return strings.err_no_tracking;
      case 'provider_unavailable':  return strings.err_provider;
      case 'no_endpoint':           return strings.err_generic;
      default:                      return strings.err_generic;
    }
  }

  // Per-instance controller
  function createInstance(root) {
    var configRaw = root.getAttribute('data-config') || '{}';
    var config;
    try { config = JSON.parse(configRaw); } catch (e) { config = {}; }
    var strings = (config && config.strings) || {};

    var form = root.querySelector('[data-ot-form]');
    var formError = root.querySelector('[data-ot-form-error]');
    var resetBtn = root.querySelector('[data-ot-reset]');
    var retryBtn = root.querySelector('[data-ot-retry]');
    var globeWrap = root.querySelector('[data-ot-globe]');
    var noteEl = root.querySelector('[data-ot-note]');
    var defaultNoteHTML = noteEl ? noteEl.innerHTML : '';

    var lastSubmitted = null;
    var globeApi = null;          // returned by renderGlobe
    var rafToken = null;

    var hasWebGL = detectWebGL();
    var motionOk = !reducedMotion();
    var canRender3D = hasWebGL && motionOk && globeWrap;

    function disableForm(disabled) {
      if (!form) return;
      var els = form.querySelectorAll('input,button');
      for (var i = 0; i < els.length; i++) els[i].disabled = !!disabled;
    }

    function reset() {
      if (globeApi && typeof globeApi.cleanup === 'function') {
        try { globeApi.cleanup(); } catch (e) {}
      }
      globeApi = null;
      if (formError) formError.textContent = '';
      setState(root, 'form');
    }

    function renderTracking(data) {
      paintTimeline(root, data.status, data.progress);
      paintInfo(root, data);
      if ((config.showHistory !== false) && data.events) paintEvents(root, data.events);
      paintProducts(root, data.products);
      carrierFallback(root, data, strings);
      // Once it's at our warehouse, tell the customer it's coming to their door.
      if (noteEl) {
        noteEl.innerHTML = (data.status === 'delivered_warehouse')
          ? 'Your order has reached our warehouse — it will be shipped to your door within <strong>3 days</strong>.'
          : defaultNoteHTML;
      }
      setState(root, 'tracking');

      if (canRender3D && config.globeModuleUrl) {
        loadGlobeModule(config.globeModuleUrl).then(function (mod) {
          if (!mod || typeof mod.renderGlobe !== 'function') return;
          try {
            globeApi = mod.renderGlobe(globeWrap, {
              origin: data.origin,
              destination: data.destination,
              current: data.current,
              texture: config.globeTexture || 'night-earth',
              accentHex: config.accentHex || '#E8B923',
              enablePlane: config.enable3dPlane !== false,
              progress: typeof data.progress === 'number' ? data.progress : (STATUS_TO_PROGRESS[data.status] || 0)
            });
          } catch (err) {
            // Renderer failed — keep the textual view.
            // eslint-disable-next-line no-console
            console.warn('[order-tracker] globe render failed', err);
          }
        }).catch(function (err) {
          // eslint-disable-next-line no-console
          console.warn('[order-tracker] failed to load globe module', err);
        });
      }
    }

    function onSubmit(e) {
      e.preventDefault();
      if (!form) return;
      // New flow: look up by order number (optional) + email/phone.
      // A legacy single "trackingNumber" field is still supported if present.
      var orderInput = form.querySelector('[name="orderNumber"]');
      var contactInput = form.querySelector('[name="contact"]');
      var legacyInput = form.querySelector('[name="trackingNumber"]');

      var orderNumber = orderInput ? orderInput.value.trim().replace(/^#+/, '') : '';
      var contact = contactInput ? contactInput.value.trim() : '';
      var legacy = legacyInput ? legacyInput.value.trim().replace(/^#+/, '') : '';

      var body, seed;
      if (orderInput) {
        // Order number is required; email/phone is optional (extra verification).
        if (!orderNumber || orderNumber.length < 3) {
          if (formError) formError.textContent = 'Please enter your order number.';
          return;
        }
        if (contact && !(isEmail(contact) || isPhone(contact))) {
          if (formError) formError.textContent = 'Please enter a valid email or phone number, or leave it blank.';
          return;
        }
        body = { orderNumber: orderNumber, contact: contact };
        seed = orderNumber;
      } else {
        // Legacy tracking-number-only form.
        if (!legacy || legacy.length < 5) {
          if (formError) formError.textContent = strings.err_invalid || 'Please enter a valid tracking number.';
          return;
        }
        body = { trackingNumber: legacy };
        seed = legacy;
      }

      if (formError) formError.textContent = '';
      lastSubmitted = body;
      disableForm(true);
      setState(root, 'loading');

      // Prefetch the globe module while the API call is in flight.
      if (canRender3D && config.globeModuleUrl) {
        loadGlobeModule(config.globeModuleUrl).catch(function () { /* non-fatal */ });
      }

      var demoEnabled = config.demoMode !== false; // default on
      fetchTracking(config.apiEndpoint, lastSubmitted).then(function (data) {
        disableForm(false);
        // If demo mode is on and the backend didn't return a usable result,
        // still show the showcase rather than an empty view.
        if (demoEnabled && (!data || !data.status)) {
          renderTracking(buildDemoData(seed, config));
          return;
        }
        renderTracking(data || {});
      }, function (err) {
        disableForm(false);
        // Demo mode = always show a polished result, whatever the backend says
        // (no backend deployed, network error, order not found, no tracking yet,
        // provider down, etc.). Turn demo mode OFF in the section settings once
        // your live backend is ready, so real statuses and errors show instead.
        if (demoEnabled) {
          renderTracking(buildDemoData(seed, config));
          return;
        }
        var carrierUrl = (err && err.body && err.body.carrierTrackingUrl) || null;
        showError(root, errorCodeToMessage(err && err.code, strings), carrierUrl);
      });
    }

    if (form) form.addEventListener('submit', onSubmit);
    if (resetBtn) resetBtn.addEventListener('click', reset);
    if (retryBtn) retryBtn.addEventListener('click', reset);

    // Pause/resume globe animation on tab visibility
    function onVis() {
      if (!globeApi) return;
      if (document.hidden && typeof globeApi.pause === 'function') globeApi.pause();
      else if (typeof globeApi.resume === 'function') globeApi.resume();
    }
    document.addEventListener('visibilitychange', onVis);

    // Theme editor lifecycle
    function onSectionUnload(evt) {
      if (evt && evt.detail && evt.detail.sectionId !== root.getAttribute('data-section-id')) return;
      if (globeApi && typeof globeApi.cleanup === 'function') {
        try { globeApi.cleanup(); } catch (e) {}
      }
      document.removeEventListener('visibilitychange', onVis);
    }
    document.addEventListener('shopify:section:unload', onSectionUnload);

    // Initial state
    setState(root, 'form');

    return {
      destroy: onSectionUnload
    };
  }

  var OrderTracker = {
    _instances: new WeakMap(),
    init: function (root) {
      if (!root || this._instances.has(root)) return;
      var inst = createInstance(root);
      this._instances.set(root, inst);
    }
  };

  window.OrderTracker = OrderTracker;
  document.dispatchEvent(new CustomEvent('order-tracker:ready'));
})();
